import { Box, Button, Flex, Heading, Text, useToast } from '@chakra-ui/react';
import SelectInput from 'components/inputs/SelectInput';
import { useMarketingViews } from 'hooks/useMarketingViews';
import {
  getMarketingKpisApi,
  getMarketingKpisLatestApi,
  getMarketingMonthlySpendDataApi,
  getMarketingDailySpendDataApi,
  updateMarketingKpisApi,
} from 'api/marketingApi';
import {
  currencyFormat, getCurrencyFormat,
  getPercentageFormat, percentageFormat,
} from 'components/Number';
import {
  CurrencyNumberInput,
  PercentageNumberInput,
} from 'components/NumberInput';
import OverlaySpinnerWrapper from 'components/OverlaySpinner';
import Card from 'components/card/Card';
import EditKpiCollection from 'components/underwriting/EditKpiCollection';
import cloneDeep from 'lodash.clonedeep';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import DateInputHeader from '../DateInputHeader';
import SpendBarChart from './SpendBarChart';
import SpendBarChart2 from './SpendBarChart2';
import SpendBySourceBarChart from "./SpendBySourceBarChart";
import MarketingSpendSection from './MarketingSpendSection';

const getKpiDisplayConfig = ({ date }) => {

  const config = [
    {
      displayName: 'Last Marketing Spend',
      row: 1,
      items: [
        {
          kpiProp: 'lastDaySpent',
          label: moment(date).clone().format('MMM DD, YYYY'),
          format: getCurrencyFormat(0),
          fieldElement: CurrencyNumberInput,
        },
      ],
    },
    {
      displayName: 'Avg Daily Marketing Spend',
      row: 1,
      items: [
        {
          kpiProp: 'avgDailySpend7Days',
          label: '7 Days',
          format: getCurrencyFormat(0),
          precision: 2,
          fieldElement: CurrencyNumberInput,
        },
        {
          kpiProp: 'avgDailySpend30Days',
          label: '30 Days',
          format: getCurrencyFormat(0),
          fieldElement: CurrencyNumberInput,
        },
      ],
    },
    {
      displayName: 'CAGR (Compound Annual Growth Rate)',
      tooltip: 'CAGR is calculated by comparing the selected month to the same month one year ago',
      row: 1,
      items: [
        {
          kpiProp: 'cagr',
          label: '',
          format: getPercentageFormat(1),
          fieldElement: PercentageNumberInput,
        },
      ],
    },

    {
      displayName: 'Month to Date',
      row: 2,
      items: [
        {
          kpiProp: 'selectedMonthToDateSpend',
          label: 'Selected Month',
          format: getCurrencyFormat(0),
          fieldElement: CurrencyNumberInput,
        },
        {
          kpiProp: 'prevMonthToDateSpend',
          label: 'Prev Month',
          format: getCurrencyFormat(0),
          fieldElement: CurrencyNumberInput,
        },
        {
          kpiProp: 'prevYearMonthToDateSpend',
          label: 'Last Year',
          format: getCurrencyFormat(0),
          fieldElement: CurrencyNumberInput,
          tooltip: 'The selected month one year ago',
        },
      ],
    },
    {
      displayName: 'Annual Run Rate',
      tooltip: 'The selected full month\'s marketing, multiplied by 12, to estimate a full-year projection',
      row: 2,
      items: [
        {
          kpiProp: 'annuallyRunRate',
          label: '',
          format: getCurrencyFormat(0),
          fieldElement: CurrencyNumberInput,
        },
      ],
    },

    {
      displayName: 'Full Month Comparison',
      row: 3,
      items: [
        {
          kpiProp: 'selectedMonthSpend',
          label: 'Selected Month',
          format: getCurrencyFormat(0),
          fieldElement: CurrencyNumberInput,
          tooltip: 'This number also includes our prediction',
        },
        {
          kpiProp: 'prevFullMonthSpend',
          label: 'Prev Month',
          format: getCurrencyFormat(0),
          fieldElement: CurrencyNumberInput,
        },
        {
          kpiProp: 'prevYearFullMonthSpend',
          label: 'Last Year',
          format: getCurrencyFormat(0),
          fieldElement: CurrencyNumberInput,
          tooltip: 'The selected month one year ago',
        },
      ],
    },
    {
      displayName: 'Last 12 Months Spend',
      tooltip: 'The selected month plus the previous 11 months',
      row: 3,
      items: [
        {
          kpiProp: 'last_12MonthsSpend',
          label: '',
          format: getCurrencyFormat(0),
          fieldElement: CurrencyNumberInput,
          // compareValue: 100000, // marketingKpis.???,
        },
      ],
    },

    // {
    //   displayName: 'Last Day Spend',
    //   row: 1,
    //   items: [
    //     {
    //       kpiProp: 'lastDaySpent',
    //       label: moment(date).clone().format('MMM DD, YYYY'),
    //       format: currencyFormat,
    //       fieldElement: CurrencyNumberInput,
    //     },
    //   ],
    // },
    // {
    //   displayName: 'Average Daily Spend',
    //   row: 1,
    //   items: [
    //     {
    //       kpiProp: 'avgDailySpend7Days',
    //       label: '7 Days',
    //       format: currencyFormat,
    //       fieldElement: CurrencyNumberInput,
    //     },
    //     {
    //       kpiProp: 'avgDailySpend30Days',
    //       label: '30 Days',
    //       format: currencyFormat,
    //       fieldElement: CurrencyNumberInput,
    //     },
    //   ],
    // },
    // {
    //   displayName: 'Marketing Monthly Run Rate',
    //   row: 1,
    //   items: [
    //     {
    //       kpiProp: 'monthlyRunRate',
    //       format: currencyFormat,
    //       fieldElement: CurrencyNumberInput,
    //     },
    //   ],
    // },
    // {
    //   displayName: 'Marketing Annual Run Rate',
    //   row: 1,
    //   items: [
    //     {
    //       kpiProp: 'annualRunRate',
    //       format: currencyFormat,
    //       fieldElement: CurrencyNumberInput,
    //     },
    //   ],
    // },
    // {
    //   displayName: 'Total Marketing Spend',
    //   row: 2,
    //   items: [
    //     {
    //       kpiProp: 'totalMarketingSpend',
    //       label: 'Last Full Month',
    //       format: currencyFormat,
    //       fieldElement: CurrencyNumberInput,
    //     },
    //   ],
    // },
    // {
    //   displayName: 'User Acquisition Spend',
    //   row: 2,
    //   items: [
    //     {
    //       kpiProp: 'uaSpendPct',
    //       format: percentageFormat,
    //       fieldElement: PercentageNumberInput,
    //     },
    //   ],
    // },
    // {
    //   displayName: 'Total Spend',
    //   row: 2,
    //   items: [
    //     {
    //       kpiProp: 'totalSpendLast30Days',
    //       label: 'Last 30 Days',
    //       format: currencyFormat,
    //       fieldElement: CurrencyNumberInput,
    //     },
    //     {
    //       kpiProp: 'totalSpendLastMonth',
    //       label: 'Last Full Month',
    //       format: currencyFormat,
    //       fieldElement: CurrencyNumberInput,
    //     },
    //   ],
    // },
  ];
  return config;
};

const Marketing = ({ isPlayground }) => {
  const urlParams = useParams();
  let [searchParams] = useSearchParams();

  const customerId = isPlayground
    ? searchParams.get('customerId')
    : urlParams.customerId;

  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(moment().subtract(1, 'days').format('YYYY-MM-DD'));
  const [marketingKpis, setMarketingKpis] = useState(null);
  const [marketingMonthlySpendData, setMarketingMonthlySpendData] = useState(null);
  const [marketingDailySpendData, setMarketingDailySpendData] = useState(null);
  const [marketingViewId, setMarketingViewId] = useState('');
  const { options: marketingViewOptions } = useMarketingViews(customerId);
  const toast = useToast();

  useEffect(() => {
    if (!customerId) {
      return;
    }
    loadData();
  }, [date, customerId, marketingViewId]);

  const loadData = async () => {
    setLoading(true);
    const promises = [
      getMarketingKpis({ isLatest: false }),
      getMarketingMonthlySpendData(),
      getMarketingDailySpendData(),
    ];

    try {
      const [
        _marketingKpis,
        _marketingMonthlySpendData,
        _marketingDailySpendData
      ] = await Promise.all(promises);

      setMarketingKpis(_marketingKpis);
      setMarketingMonthlySpendData(_marketingMonthlySpendData);
      setMarketingDailySpendData(_marketingDailySpendData);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getMarketingKpis = async ({ isLatest }) => {
    const apiFunc = isLatest ? getMarketingKpisLatestApi : getMarketingKpisApi;

    try {
      const _marketingKpis = await apiFunc({
        customerId,
        date,
        saveInputs: !isPlayground,
        marketingViewId,
      });
      return _marketingKpis;
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch marketing KPIs',
        status: 'error',
      });
      throw error;
    }
  };

  const getMarketingMonthlySpendData = async () => {
    try {
      const _marketingMonthlySpendData = await getMarketingMonthlySpendDataApi({
        customerId,
        date,
        marketingViewId,
      });

      return _marketingMonthlySpendData;
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch marketing monthly spend data',
        status: 'error',
      });
      throw error;
    }
  };

  const getMarketingDailySpendData = async () => {
    try {
      const _marketingDailySpendData = await getMarketingDailySpendDataApi({
        customerId,
        date,
        marketingViewId,
      });

      return _marketingDailySpendData;
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch marketing daily spend data',
        status: 'error',
      });
      throw error;
    }
  };
  const updateMarketingKpis = async (_marketingKpis) => {
    setLoading(true);
    try {
      await updateMarketingKpisApi({
        customerId,
        date,
        marketingKpis: _marketingKpis,
      });
      toast({
        title: 'Success',
        description: 'Marketing KPIs updated',
        status: 'success',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update marketing KPIs',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const onKpiSave = async ({ kpiProp, kpi }) => {
    let _marketingKpis = cloneDeep(marketingKpis);
    _marketingKpis[kpiProp] = kpi;

    await updateMarketingKpis(_marketingKpis);
    setMarketingKpis(_marketingKpis);
  };

  const onFetchLatestData = async () => {
    setLoading(true);
    try {
      const _marketingKpis = await getMarketingKpis({ isLatest: true });
      setMarketingKpis(_marketingKpis);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch latest marketing KPIs',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <OverlaySpinnerWrapper show={loading} isFixed={true}>
      <Box minW={'container.lg'} maxW={'max'}>
        <DateInputHeader
          date={date}
          onDateChange={setDate}
          showCustomerSelect={isPlayground}
          rightButtonElm={
            <Flex gap={3} align="center">
              {marketingViewOptions.length > 0 && (
                <Box w={220}>
                  <SelectInput
                    value={marketingViewId}
                    options={marketingViewOptions}
                    onChange={setMarketingViewId}
                    placeholder="Marketing view"
                  />
                </Box>
              )}
              <Button variant={'brand'} onClick={onFetchLatestData}>
                Fetch Latest Data
              </Button>
            </Flex>
          }
        />
        {marketingKpis && customerId && (
          <Card>
            {' '}
            <Heading variant={'sectionHeader'} borderColor={'pink.500'}>
              Marketing
            </Heading>
            <Flex mt={50} gap={150} h={350} justifyContent={'space-between'}>
              <Box flex={1}>
                <SpendBarChart data={marketingMonthlySpendData}/>
              </Box>
            </Flex>
            <Flex mt={50} gap={150} h={350} justifyContent={'space-between'}>
              <Box flex={1}>
                <SpendBarChart2 data={marketingMonthlySpendData}/>
              </Box>
            </Flex>
            <Flex mt={50} gap={150} h={350} justifyContent={'space-between'}>
              <Box flex={1}>
                <SpendBySourceBarChart data={marketingDailySpendData} date={date}/>
              </Box>
            </Flex>
            <Box mt={50}>
              <Text fontWeight={800}>KPIs</Text>
              <EditKpiCollection
                  kpis={marketingKpis}
                  config={getKpiDisplayConfig({date: marketingKpis.latestDate?.value})}
                  // config={getKpiDisplayConfig({ date })}
                  onKpiSave={onKpiSave}
                  hasOriginalValues={true}
                  hideEditButton={isPlayground}
              />
            </Box>
          </Card>
        )}
      </Box>
      {customerId && <MarketingSpendSection customerId={customerId} />}
    </OverlaySpinnerWrapper>
  );
};

Marketing.propTypes = {
  isPlayground: PropTypes.bool,
};

export default Marketing;
